import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, X, Check, Copy, Edit3, FileText, Play, Pause, Trash2, LogIn } from "lucide-react";
import { GROUPS, ACTIVITIES, ACTIVITY_TO_MODES, ACTIVITY_LABELS, LODGING_MODE_IDS } from "@/lib/activityCatalog";

export const SUPERADMIN_MODULES = [
  { id: "casse", label: "Cassa" },
  { id: "magazzino", label: "Magazzino" },
  { id: "multi_sede", label: "Multi-Punto Vendita" },
  { id: "reminders_whatsapp", label: "Reminder" },
  { id: "operatori", label: "Operatori" },
  { id: "palmare", label: "Palmare" },
  { id: "appuntamenti_multisede", label: "Appuntamenti" },
];

export const defaultModules = () => Object.fromEntries((SUPERADMIN_MODULES || []).map((m) => [m.id, true]));

export function ModuleToggles({ value, onChange, testidPrefix = "admin-mod" }) {
  const mods = { ...defaultModules(), ...(value || {}) };
  return (
    <div className="grid grid-cols-2 gap-2">
      {(SUPERADMIN_MODULES || []).map((m) => {
        const active = mods[m.id] !== false;
        return (
          <button
            key={m.id}
            type="button"
            data-testid={`${testidPrefix}-${m.id}`}
            onClick={() => onChange({ ...mods, [m.id]: !active })}
            className={`flex items-center gap-2 h-11 px-3 border-2 rounded-md text-sm text-left transition ${active ? "border-velta-ink bg-velta-ink/5" : "border-velta-border hover:border-velta-ink"}`}
          >
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${active ? "border-velta-ink bg-velta-ink" : "border-velta-border"}`}>
              {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </div>
            <span className="flex-1">{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [modes, setModes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const [tokenPreview, setTokenPreview] = useState(null);
  const [contractTarget, setContractTarget] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { beginImpersonation } = useAuth() || {};
  const clients = Array.isArray(items) ? items : [];
  const licenses = [];
  const tenants = clients;

  const load = () => api.get("/admin/clients")
    .then((r) => setItems(Array.isArray(r?.data) ? r.data : []))
    .catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/modes?include_hidden=true").then((r) => setModes(Array.isArray(r?.data) ? r.data : [])).catch(() => setModes([]));
  }, []);

  useEffect(() => {
    const req = location.state?.fromRequest;
    if (req) {
      navigate("/admin/superadmin", { replace: true, state: { fromRequest: req } });
    }
  }, [location.state]);

  const toggle = async (t) => {
    try {
      await api.patch(`/admin/clients/${t.id}?active=${!t.active}`);
      toast.success(!t.active ? "Tenant attivato" : "Tenant disattivato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const impersonate = async (t) => {
    if (!window.confirm(`Aprire il dashboard di «${t.business_name}» come titolare?\n\nL'accesso viene registrato (tuo utente, data, cliente). Non usare un locale reale come banco di prova: per i test c'è la Cassa Demo.`)) return;
    try {
      const r = await api.post(`/admin/clients/${t.id}/impersonate`);
      beginImpersonation?.(r.data.token, r.data.user);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile aprire il cliente");
    }
  };

  const toggleModule = async (t, modId) => {
    const current = { ...defaultModules(), ...(t.reseller_modules || {}) };
    const next = { ...current, [modId]: current[modId] === false };
    try {
      await api.patch(`/admin/clients/${t.id}/modules`, { modules: next });
      toast.success(next[modId] ? "Modulo attivato" : "Modulo disattivato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore moduli");
    }
  };

  return (
    <div className="p-8 lg:p-12">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Clienti</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Anagrafica clienti</h1>
          <p className="text-velta-mute mt-2">Registra il tenant, attiva i moduli e genera il token di piattaforma. Nessuna chiave da inserire lato cliente.</p>
        </div>
        <button data-testid="admin-client-new-btn" onClick={() => navigate("/admin/superadmin")} className="h-12 px-6 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
          <Plus className="w-4 h-4" /> Nuovo cliente
        </button>
      </div>

      <div className="bg-white border border-velta-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-velta-bg border-b border-velta-border">
            <tr className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute">
              <th className="px-5 py-4">Ragione sociale</th>
              <th className="px-5 py-4">Dominio / tenant</th>
              <th className="px-5 py-4">Email titolare</th>
              <th className="px-5 py-4">P.IVA</th>
              <th className="px-5 py-4">Scontrini</th>
              <th className="px-5 py-4">Attivato</th>
              <th className="px-5 py-4">Stato</th>
              <th className="px-5 py-4">Attività</th>
              <th className="px-5 py-4">Ciclo</th>
              <th className="px-5 py-4">Importo</th>
              <th className="px-5 py-4">Scadenza</th>
              <th className="px-5 py-4">Pagamento</th>
              <th className="px-5 py-4">Contratto</th>
              <th className="px-5 py-4">Moduli</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody data-testid="admin-clients-tbody">
            {(clients?.length ?? 0) === 0 && (
              <tr><td colSpan={15} className="px-5 py-12 text-center text-velta-mute">Nessun cliente ancora creato.</td></tr>
            )}
            {(clients || []).map((t) => {
              const t_modes = t?.modes || (t?.mode ? [t.mode] : []);
              const activityLabel = ACTIVITY_LABELS?.[t?.activity_type] || '—';
              const contractStatus = t.contract_status || 'bozza';
              const statusColors = {
                'attivo': 'bg-velta-success/10 text-velta-success',
                'bozza': 'bg-yellow-100 text-yellow-700',
                'sospeso': 'bg-orange-100 text-orange-700',
                'scaduto': 'bg-red-100 text-red-700',
              };
              const paymentColors = {
                'pagato': 'text-velta-success',
                'in_ritardo': 'text-velta-error',
                'pending': 'text-velta-mute',
              };
              return (
                <tr key={t.id} className="border-b border-velta-border last:border-0 hover:bg-velta-bg">
                  <td className="px-5 py-4 font-medium align-top">
                    <div>{t.business_name}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button data-testid={`admin-edit-client-${t.id}`} onClick={() => setEditing({ ...t, modes: t_modes, domain: t.domain || t.slug || "", license_expires_at: t.license_expires_at || t.next_due_date || "", modules: { ...defaultModules(), ...(t.reseller_modules || {}) } })} className="h-8 px-3 rounded-md bg-[#1E2D5B] text-white text-xs font-medium inline-flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Modifica</button>
                      <button
                        type="button"
                        data-testid={`admin-suspend-client-${t.id}`}
                        onClick={() => {
                          const off = t.active === false || contractStatus === "sospeso";
                          const msg = off
                            ? `Riattivare "${t.business_name}"?`
                            : `Sospendere "${t.business_name}"? Non potrà entrare. I dati restano.`;
                          if (!window.confirm(msg)) return;
                          toggle(t);
                        }}
                        className={`h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1 border ${t.active === false || contractStatus === "sospeso" ? "border-emerald-300 text-emerald-700" : "border-red-300 text-red-700"}`}
                      >
                        {t.active === false || contractStatus === "sospeso" ? <><Play className="w-3.5 h-3.5" /> Riattiva</> : <><Pause className="w-3.5 h-3.5" /> Sospendi</>}
                      </button>
                    </div>
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-velta-mute">{t.domain || t.slug || "—"}</td>
                  <td className="px-5 py-4 text-velta-mute">{t.owner_email}</td>
                  <td className="px-5 py-4 font-mono text-xs">{t.vat_number || "—"}</td>
                  <td className="px-5 py-4 font-mono">{t.orders_count}</td>
                  <td className="px-5 py-4 text-velta-mute text-xs">{formatDate(t.created_at)}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded ${statusColors[contractStatus] || 'bg-velta-bg'}`}>
                      {contractStatus}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs">{activityLabel}</td>
                  <td className="px-5 py-4">
                    <span className="text-xs font-mono uppercase">
                      {t.billing_cycle || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-4 font-mono text-xs">
                    {t.agreed_amount_eur != null && Number.isFinite(Number(t.agreed_amount_eur))
                      ? `${Number(t.agreed_amount_eur).toFixed(2)}€` : '—'}
                  </td>
                  <td className="px-5 py-4 text-xs text-velta-mute">{t.next_due_date || t.license_expires_at || '—'}</td>
                  <td className={`px-5 py-4 text-xs font-medium ${paymentColors[t.payment_status] || 'text-velta-mute'}`}>
                    {t.payment_status || '—'}
                  </td>
                  <td className="px-5 py-4">
                    <button
                      data-testid={`admin-contract-${t.id}`}
                      onClick={() => setContractTarget(t)}
                      className="text-xs font-medium text-[#1E2D5B] hover:underline inline-flex items-center gap-1"
                    >
                      <FileText className="w-3.5 h-3.5" /> Gestisci
                    </button>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {(SUPERADMIN_MODULES || []).map((m) => {
                        const on = { ...defaultModules(), ...(t.reseller_modules || {}) }[m.id] !== false;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            data-testid={`admin-mod-toggle-${t.id}-${m.id}`}
                            onClick={() => toggleModule(t, m.id)}
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${on ? "bg-[#1E2D5B] text-white border-[#1E2D5B]" : "bg-white text-velta-mute border-velta-border"}`}
                            title={`${on ? "Disattiva" : "Attiva"} ${m.label}`}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        data-testid={`admin-impersonate-${t.id}`}
                        onClick={() => impersonate(t)}
                        className="text-xs font-medium text-[#1E2D5B] hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                        title="Apri il dashboard di questo cliente come titolare"
                      >
                        <LogIn className="w-3.5 h-3.5" /> Apri come titolare
                      </button>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer" title={t.active ? "Disattiva tenant" : "Attiva tenant"}>
                        <input
                          type="checkbox"
                          data-testid={`admin-toggle-client-${t.id}`}
                          checked={!!t.active}
                          onChange={() => toggle(t)}
                          className="sr-only"
                        />
                        <span className={`relative inline-block h-5 w-9 rounded-full transition ${t.active ? "bg-[#2F9E77]" : "bg-velta-border"}`}>
                          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${t.active ? "left-4.5 left-[18px]" : "left-0.5"}`} />
                        </span>
                      </label>
                      <button
                        data-testid={`admin-activate-tenant-${t.id}`}
                        onClick={() => setActivateTarget(t)}
                        className="text-xs font-medium text-[#1E2D5B] hover:underline inline-flex items-center gap-1"
                      >
                        <Play className="w-3.5 h-3.5" /> Attiva Tenant
                      </button>
                      <button
                        data-testid={`admin-delete-client-${t.id}`}
                        onClick={async () => {
                          const isTest = /^TEST[\s_]/i.test(t.business_name || '');
                          const msg = isTest
                            ? `Eliminare DEFINITIVAMENTE "${t.business_name}"? Verranno cancellati TUTTI i dati (categorie, prodotti, ordini, appuntamenti). Operazione irreversibile.`
                            : `Sospendere "${t.business_name}"? Il cliente non potrà accedere ma i dati saranno preservati.\n\n(Per hard-delete usa da CLI o rinomina il tenant con prefisso TEST_)`;
                          if (!window.confirm(msg)) return;
                          try {
                            await api.delete(`/admin/clients/${t.id}${isTest ? '?hard=true' : ''}`);
                            toast.success(isTest ? "Cliente eliminato definitivamente" : "Cliente sospeso");
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
                        }}
                        className="text-velta-error hover:text-red-700"
                        title={/^TEST[\s_]/i.test(t.business_name || '') ? "Elimina definitivamente (tenant di test)" : "Sospendi cliente"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <EditClientModal client={editing} modes={modes} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {contractTarget && <ContractModal tenantId={contractTarget.id} businessName={contractTarget.business_name} onClose={() => setContractTarget(null)} onSaved={() => { setContractTarget(null); load(); }} />}
      {activateTarget && (
        <ActivateTenantModal
          client={activateTarget}
          onClose={() => setActivateTarget(null)}
          onDone={(data) => {
            setActivateTarget(null);
            setTokenPreview(data);
            load();
          }}
        />
      )}
      {tokenPreview && <TenantTokenModal data={tokenPreview} onClose={() => setTokenPreview(null)} />}
    </div>
  );
}

// Attività per il wizard admin: catalogo pubblico + combo pet "entrambe"
const ADMIN_ACTIVITIES = {
  ...ACTIVITIES,
  negozi: [
    ...ACTIVITIES.negozi.slice(0, 2),
    { id: "entrambe", label: "Negozio Animali + Toelettatura", icon: ACTIVITIES.negozi[1].icon },
    ...ACTIVITIES.negozi.slice(2),
  ],
  eventi: [
    ...ACTIVITIES.eventi,
    { id: "alloggio_dedicato", label: "Alberghiero dedicato", icon: ACTIVITIES.eventi[2].icon },
  ],
};

function ActivateTenantModal({ client, onClose, onDone }) {
  const [modules, setModules] = useState({ ...defaultModules(), ...(client.reseller_modules || {}) });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/admin/clients/${client.id}/activate`, { modules });
      toast.success("Tenant attivato");
      onDone({ ...r.data, business_name: client.business_name, owner_email: client.owner_email });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore attivazione");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">SuperAdmin</div>
            <h3 className="font-heading text-xl font-semibold">Attiva Tenant</h3>
            <p className="text-sm text-velta-mute mt-1">{client.business_name} — genera un token univoco e associa i moduli.</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-5">
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Moduli da associare</label>
          <ModuleToggles value={modules} onChange={setModules} testidPrefix="activate-mod" />
        </div>
        <button data-testid="activate-tenant-confirm" onClick={submit} disabled={loading} className="w-full h-12 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile disabled:opacity-50">
          {loading ? "Attivazione…" : "Genera token e attiva"}
        </button>
      </div>
    </div>
  );
}

function TenantTokenModal({ data, onClose }) {
  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copiato"); }
    catch { toast.info("Copia manuale richiesta"); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-4">
          <div className="w-12 h-12 bg-[#2F9E77] rounded-full flex items-center justify-center mx-auto mb-3"><Check className="w-7 h-7 text-white" strokeWidth={3} /></div>
          <h3 className="font-heading text-2xl font-semibold">Tenant attivo</h3>
          <p className="text-sm text-velta-mute">{data.business_name || "Cliente"} — il cliente accede con email e password, senza chiave.</p>
        </div>
        <div className="bg-[#1E2D5B]/5 border-2 border-[#1E2D5B] rounded-md p-4 mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Token univoco (uso interno piattaforma)</div>
          <div className="flex items-center justify-between gap-3">
            <code className="font-mono text-sm break-all">{data.tenant_token}</code>
            <button onClick={() => copy(data.tenant_token)} className="shrink-0"><Copy className="w-5 h-5" /></button>
          </div>
        </div>
        <button onClick={onClose} className="w-full h-12 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Chiudi</button>
      </div>
    </div>
  );
}

function NewClientModal({ modes, fromRequest, onClose, onCreated }) {
  const prefillNotes = () => {
    if (!fromRequest) return "";
    const needs = (Array.isArray(fromRequest.answers_readable) ? fromRequest.answers_readable : []).map(x => x.label + (x.value !== "Sì" ? `: ${x.value}` : "")).join("; ");
    return `Da richiesta preventivo del ${new Date(fromRequest.created_at).toLocaleDateString("it-IT")}` +
      (needs ? ` — Esigenze: ${needs}` : "") + (fromRequest.notes ? ` — Note cliente: ${fromRequest.notes}` : "");
  };
  const prefillModules = () => {
    const a = fromRequest?.answers || {};
    const list = [];
    if (a.magazzino) list.push("optional_magazzino_avanzato");
    if (a.fidelity) list.push("optional_fidelity_card");
    return list;
  };
  const [step, setStep] = useState(1);
  const [activityGroup, setActivityGroup] = useState(() => {
    if (fromRequest) {
      const g = fromRequest.group === "ospitalita" ? "eventi" : fromRequest.group;
      if (ADMIN_ACTIVITIES[g]) return g;
    }
    return "servizi";
  });
  const [f, setF] = useState({
    // Step 1: Anagrafica
    business_name: fromRequest?.business_name || "",
    owner_email: fromRequest?.email || "",
    owner_password: "",
    owner_name: fromRequest?.contact_name || "",
    fiscal_code: "", vat_number: "",
    address: fromRequest?.city || "",
    phone: fromRequest?.phone || "",
    notes: prefillNotes(),
    domain: "",
    modules: (() => {
      const mods = defaultModules();
      if (fromRequest?.answers?.magazzino === false) mods.magazzino = false;
      return mods;
    })(),
    // Step 2: Attività + Piano + Moduli
    activity_type: fromRequest && ACTIVITY_TO_MODES[fromRequest.activity_id] ? fromRequest.activity_id : "toelettatura",
    plan_tier: "base",
    billing_cycle: "mensile",
    active_optional_modules: prefillModules(),
    // Step 3: Importo + Scadenza + Pagamento
    agreed_amount_eur: "",
    next_due_date: "",
    payment_method: "bonifico",
    payment_status: "pending",
    // System (legacy fields)
    valid_days: 365, max_users: 5, max_stores: 1,
    activate_now: false,  // se true, imposta contract_status=attivo dopo la creazione
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const OPTIONAL_MODULES = [
    { id: "optional_magazzino_avanzato", label: "Magazzino avanzato" },
    { id: "optional_fidelity_card", label: "Fidelity card" },
    { id: "optional_multi_operator", label: "Multi-operatore con PIN" },
    { id: "optional_sdi_direct", label: "SDI diretto Aruba / Fattura24" },
  ];

  const activityModes = () => ACTIVITY_TO_MODES[f.activity_type] || ["negozio"];

  const validateStep1 = () => {
    if (!f.business_name.trim()) return "Ragione sociale obbligatoria";
    if (!f.owner_email.trim() || !/@/.test(f.owner_email)) return "Email titolare non valida";
    if (!f.owner_password || f.owner_password.length < 6) return "Password minimo 6 caratteri";
    return null;
  };
  const validateStep2 = () => {
    if (!ACTIVITY_TO_MODES[f.activity_type]) return "Scegli un'attività";
    return null;
  };

  const next = () => {
    const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
    if (err) return toast.error(err);
    setStep(step + 1);
  };
  const back = () => setStep(Math.max(1, step - 1));

  const submit = async () => {
    setLoading(true);
    try {
      // 1. Crea cliente + owner + license (endpoint esistente)
      const r = await api.post("/admin/clients", {
        business_name: f.business_name,
        owner_email: f.owner_email,
        owner_password: f.owner_password,
        owner_name: f.owner_name || f.business_name,
        modes: activityModes(),
        fiscal_code: f.fiscal_code,
        vat_number: f.vat_number,
        address: f.address,
        phone: f.phone,
        valid_days: f.valid_days,
        max_users: f.max_users,
        max_stores: f.max_stores,
        notes: f.notes,
        domain: f.domain || undefined,
        license_expires_at: f.next_due_date || undefined,
        modules: f.modules,
      });
      const newTenantId = r.data.tenant?.id || r.data.tenant_id;
      // 2. Applica contratto completo (activity_type + plan_tier + modules + importo + scadenza)
      await api.patch(`/admin/clients/${newTenantId}/contract`, {
        activity_type: f.activity_type,
        billing_cycle: f.billing_cycle,
        agreed_amount_eur: f.agreed_amount_eur === "" ? null : parseFloat(f.agreed_amount_eur),
        active_optional_modules: f.active_optional_modules,
        next_due_date: f.next_due_date || null,
        license_expires_at: f.next_due_date || null,
        payment_method: f.payment_method,
        payment_status: f.payment_status,
        contract_status: f.activate_now ? "attivo" : "bozza",
        contract_notes: f.notes || null,
        domain: f.domain || r.data.domain || undefined,
        modules: f.modules,
      });
      // 3. Se attiva ora → chiama activate (imposta activation_date + active=True)
      if (f.activate_now) {
        const act = await api.post(`/admin/clients/${newTenantId}/activate`, { modules: f.modules });
        r.data.tenant_token = act.data?.tenant_token || r.data.tenant_token;
      }
      // 4. Se il wizard parte da una richiesta di preventivo, aggiorna il suo stato
      if (fromRequest?.id) {
        try {
          await api.patch(`/admin/quote-requests/${fromRequest.id}`, { status: f.activate_now ? "attivato" : "preventivo_inviato" });
        } catch { /* non bloccante */ }
      }
      setResult({ ...r.data, ...f, tenant_id: newTenantId });
      toast.success(f.activate_now ? "Cliente creato e cassa attivata!" : "Cliente creato in stato bozza");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    }
    setLoading(false);
  };

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copiato"); }
    catch { toast.info("Copia manuale richiesta"); }
  };
  const close = () => { setResult(null); onCreated(); };

  // Preventivo copia-incolla per email/WhatsApp
  const preventivo = () => {
    if (!result) return "";
    const activityLabel = ACTIVITY_LABELS[f.activity_type] || "—";
    const cycleLabel = f.billing_cycle === "mensile" ? "al mese" : "all'anno";
    const modLabels = f.active_optional_modules
      .map(m => OPTIONAL_MODULES.find(o => o.id === m)?.label)
      .filter(Boolean);
    return `Preventivo VELTA SISTEMI per ${f.business_name}\n\n` +
      `Attività: ${activityLabel}\n` +
      `Ciclo: ${f.billing_cycle.charAt(0).toUpperCase() + f.billing_cycle.slice(1)}\n` +
      (modLabels.length > 0 ? `Moduli opzionali: ${modLabels.join(", ")}\n` : "") +
      `\nCanone software: ${f.agreed_amount_eur ? parseFloat(f.agreed_amount_eur).toFixed(2) + " € " + cycleLabel : "da concordare"}\n` +
      (f.next_due_date ? `Prima scadenza: ${new Date(f.next_due_date).toLocaleDateString("it-IT")}\n` : "") +
      `Metodo pagamento: ${f.payment_method}\n\n` +
      `Hardware e attivazione una-tantum sono fatturati separatamente e non rientrano nel canone software.\n\n` +
      `Per accettare il preventivo rispondi a questo messaggio.`;
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-auto" onClick={close}>
        <div className="bg-white rounded-md w-full max-w-lg p-6 my-8" onClick={(e) => e.stopPropagation()}>
          <div className="text-center mb-5">
            <div className="w-12 h-12 bg-[#2F9E77] rounded-full flex items-center justify-center mx-auto mb-3"><Check className="w-7 h-7 text-white" strokeWidth={3} /></div>
            <h3 className="font-heading text-2xl font-semibold mb-1">Cliente creato</h3>
            <p className="text-sm text-velta-mute">
              {f.activate_now ? "Cassa già attiva. Comunica queste credenziali al cliente." : "Contratto in stato BOZZA. Attivalo dopo aver ricevuto conferma dal cliente."}
            </p>
          </div>
          <div className="space-y-3 mb-5">
            <div className="bg-velta-bg border border-velta-border rounded-md p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Email titolare</div>
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono text-sm break-all">{result.owner_email}</div>
                <button onClick={() => copy(result.owner_email)} className="text-velta-mute hover:text-velta-ink shrink-0"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="bg-[#1E2D5B]/5 border-2 border-[#1E2D5B] rounded-md p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Password iniziale</div>
              <p className="text-sm text-velta-mute">Comunica email e password al titolare. L&apos;accesso avviene da /login — nessuna chiave di attivazione.</p>
              {result.tenant_token && (
                <div className="mt-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Token interno</div>
                  <div className="flex items-center justify-between gap-3">
                    <code className="font-mono text-xs break-all">{result.tenant_token}</code>
                    <button onClick={() => copy(result.tenant_token)} className="shrink-0"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white border border-velta-border rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Testo preventivo per email/WhatsApp</div>
                <button data-testid="wizard-copy-preventivo" onClick={() => copy(preventivo())} className="text-xs text-velta-ink hover:underline inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copia testo
                </button>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-mono bg-velta-bg p-3 rounded border border-velta-border max-h-48 overflow-auto">{preventivo()}</pre>
            </div>
          </div>
          <button data-testid="wizard-close" onClick={close} className="w-full h-12 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Chiudi</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-velta-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Nuovo cliente</div>
            <h3 className="font-heading text-xl font-semibold">Onboarding — Step {step} / 3</h3>
            {fromRequest && (
              <div className="text-[11px] text-emerald-600 font-medium mt-0.5" data-testid="wizard-from-request">
                ✓ Precompilato dalla richiesta di {fromRequest.business_name}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="wizard-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {/* Stepper */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].map(s => (
              <React.Fragment key={s}>
                <div className={`flex-1 h-1.5 rounded-full transition ${step >= s ? "bg-velta-ink" : "bg-velta-border"}`} />
                {s < 3 && <div className="w-2" />}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-6">
            <span className={step >= 1 ? "text-velta-ink font-bold" : ""}>1. Anagrafica</span>
            <span className={step >= 2 ? "text-velta-ink font-bold" : ""}>2. Attività e moduli</span>
            <span className={step >= 3 ? "text-velta-ink font-bold" : ""}>3. Preventivo</span>
          </div>
        </div>

        <div className="px-6 pb-6">
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="wizard-step-1">
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Ragione sociale *</label>
                <input data-testid="wizard-business" required value={f.business_name} onChange={set("business_name")} placeholder="Toelettatura Bau" className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Email titolare *</label>
                <input data-testid="wizard-email" required type="email" value={f.owner_email} onChange={set("owner_email")} placeholder="mario@negozio.it" className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Password iniziale *</label>
                <input data-testid="wizard-password" required minLength={6} type="text" value={f.owner_password} onChange={set("owner_password")} placeholder="min 6 caratteri" className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nome titolare</label>
                <input data-testid="wizard-owner-name" value={f.owner_name} onChange={set("owner_name")} placeholder="Mario Rossi" className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">P.IVA</label>
                <input value={f.vat_number} onChange={set("vat_number")} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Codice fiscale</label>
                <input value={f.fiscal_code} onChange={set("fiscal_code")} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Telefono</label>
                <input data-testid="wizard-phone" value={f.phone} onChange={set("phone")} placeholder="+39 340 1234567" className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Indirizzo</label>
                <input value={f.address} onChange={set("address")} placeholder="Via, città, CAP" className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Dominio / tenant</label>
                <input data-testid="wizard-domain" value={f.domain} onChange={set("domain")} placeholder="es. pizzeria-mario.veltaone.it" className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
                <div className="text-[10px] text-velta-mute mt-1">Se vuoto viene generato dalla ragione sociale. Deve essere unico.</div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="wizard-step-2">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Attività del cliente</label>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(Array.isArray(GROUPS) ? GROUPS : []).map(g => (
                    <button
                      key={g.id}
                      data-testid={`wizard-group-${g.id}`}
                      onClick={() => setActivityGroup(g.id)}
                      className={`text-xs px-3 h-8 rounded-full border-2 font-medium transition ${activityGroup === g.id ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border hover:border-velta-ink"}`}
                    >{g.label}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-52 overflow-auto pos-scroll p-1">
                  {(ADMIN_ACTIVITIES[activityGroup] || []).map(a => (
                    <button
                      key={a.id}
                      data-testid={`wizard-activity-${a.id}`}
                      onClick={() => setF({ ...f, activity_type: a.id })}
                      className={`p-2.5 border-2 rounded-md text-left flex items-center gap-2 transition ${f.activity_type === a.id ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border hover:border-velta-ink"}`}
                    >
                      <a.icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                      <span className="font-medium text-xs leading-tight">{a.label}</span>
                    </button>
                  ))}
                </div>
                {f.activity_type && ACTIVITY_TO_MODES[f.activity_type] && (
                  <div className="text-[11px] text-velta-mute mt-2" data-testid="wizard-activity-selected">
                    {f.activity_type === "bnb"
                      ? <>Selezionata: <strong className="text-velta-ink">Alloggi / Pensioni</strong>. Nessun&apos;altra cassa. Anagrafica, statistiche della struttura, Alloggiati Web e ROSS 1000.</>
                      : f.activity_type === "alloggio_dedicato"
                        ? <>Selezionata: <strong className="text-velta-ink">Alberghiero dedicato</strong>. Clienti, scansione documento, Alloggiati Web e ROSS 1000. Senza camere e senza prenotazioni.</>
                        : <>Selezionata: <strong className="text-velta-ink">{ACTIVITY_LABELS[f.activity_type]}</strong> · modalità cassa: {ACTIVITY_TO_MODES[f.activity_type].join(", ")}</>}
                  </div>
                )}
              </div>

              <div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Ciclo di fatturazione</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{id: "mensile", label: "Mensile"}, {id: "annuale", label: "Annuale"}].map(b => (
                      <button
                        key={b.id}
                        data-testid={`wizard-cycle-${b.id}`}
                        onClick={() => setF({ ...f, billing_cycle: b.id })}
                        className={`h-11 border-2 rounded-md text-sm font-medium transition ${f.billing_cycle === b.id ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border hover:border-velta-ink"}`}
                      >{b.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Moduli piattaforma</label>
                <ModuleToggles value={f.modules} onChange={(modules) => setF({ ...f, modules })} testidPrefix="wizard-mod" />
                <div className="text-[10px] text-velta-mute mt-1">Spegnere un modulo toglie solo quella funzione. Il piano non c’entra.</div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="wizard-step-3">
              <div className="bg-velta-bg border border-velta-border rounded-md p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-2">Riepilogo scelte</div>
                <div className="text-sm space-y-1">
                  <div><strong>{f.business_name}</strong> — {f.owner_email}</div>
                  <div>{ACTIVITY_LABELS[f.activity_type] || "—"} · {f.billing_cycle}</div>
                  <div className="text-xs text-velta-mute">Tenant: {f.domain || "(generato automaticamente)"}</div>
                  <div className="text-xs text-velta-mute">Moduli: {(SUPERADMIN_MODULES || []).filter((m) => f.modules?.[m.id] !== false).map((m) => m.label).join(", ")}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Importo pattuito (€)</label>
                  <input
                    data-testid="wizard-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={f.agreed_amount_eur}
                    onChange={set("agreed_amount_eur")}
                    placeholder={f.billing_cycle === "mensile" ? "es. 49.00 al mese" : "es. 490.00 all'anno"}
                    className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono text-lg outline-none focus:border-[#1E2D5B]"
                  />
                  <div className="text-[10px] text-velta-mute mt-1">Solo canone software — hardware separato</div>
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Prima scadenza</label>
                  <input
                    data-testid="wizard-due"
                    type="date"
                    value={f.next_due_date}
                    onChange={set("next_due_date")}
                    className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Metodo pagamento</label>
                  <select data-testid="wizard-payment-method" value={f.payment_method} onChange={set("payment_method")} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
                    <option value="bonifico">Bonifico</option>
                    <option value="riba">RiBa</option>
                    <option value="carta">Carta</option>
                    <option value="contanti">Contanti</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Stato iniziale pagamento</label>
                  <select data-testid="wizard-payment-status" value={f.payment_status} onChange={set("payment_status")} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
                    <option value="pending">In attesa</option>
                    <option value="pagato">Pagato</option>
                    <option value="in_ritardo">In ritardo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Note interne</label>
                <textarea data-testid="wizard-notes" rows={2} value={f.notes} onChange={set("notes")} placeholder="Note per te (non visibili al cliente)" className="w-full bg-white border border-velta-border rounded-md p-3 text-sm resize-none outline-none focus:border-[#1E2D5B]" />
              </div>

              <label className="flex items-center gap-3 p-3 border-2 border-velta-border rounded-md cursor-pointer hover:border-velta-ink transition">
                <input
                  data-testid="wizard-activate-now"
                  type="checkbox"
                  checked={f.activate_now}
                  onChange={(e) => setF({ ...f, activate_now: e.target.checked })}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Attiva la cassa subito</div>
                  <div className="text-[11px] text-velta-mute">Se disattivato, il contratto resta in stato <strong>Bozza</strong> — la cassa si attiverà solo dopo la tua conferma manuale (utile per aspettare l&apos;approvazione del preventivo).</div>
                </div>
              </label>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-velta-border flex items-center gap-2 sticky bottom-0 bg-white">
          {step > 1 && (
            <button data-testid="wizard-back" onClick={back} className="h-11 px-4 border border-velta-border rounded-md font-medium">Indietro</button>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <button data-testid="wizard-next" onClick={next} className="h-11 px-6 bg-velta-ink text-white rounded-md font-medium btn-tactile">Avanti →</button>
          ) : (
            <button data-testid="wizard-submit" onClick={submit} disabled={loading} className="h-11 px-6 bg-velta-ink hover:bg-[#162244] text-white rounded-md font-medium btn-tactile disabled:opacity-50">
              {loading ? "Creazione…" : (f.activate_now ? "Crea e attiva" : "Crea in bozza")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EditClientModal({ client, modes, onClose, onSaved }) {
  const [f, setF] = useState({ ...client });
  const toggle = (id) => {
    const cur = Array.isArray(f.modes) ? f.modes : [];
    let next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    if (LODGING_MODE_IDS.includes(id) && next.includes(id)) {
      next = next.filter((m) => m === id || !LODGING_MODE_IDS.includes(m));
    }
    let activity_type = f.activity_type;
    if (next.length === 1 && LODGING_MODE_IDS.includes(next[0])) activity_type = next[0];
    setF({ ...f, modes: next, activity_type });
  };

  const save = async () => {
    try {
      const r = await api.put(`/admin/clients/${client.id}`, {
        modes: f.modes, business_name: f.business_name, vat_number: f.vat_number,
        fiscal_code: f.fiscal_code, address: f.address, phone: f.phone,
        max_users: f.max_users, max_stores: f.max_stores,
        domain: f.domain, license_expires_at: f.license_expires_at || null,
        modules: f.modules,
        ...(f.activity_type ? { activity_type: f.activity_type } : {}),
      });
      const n = r.data?.catalog_removed?.products || 0;
      toast.success(n ? `Cliente aggiornato. Tolti ${n} articoli della cassa: l'alloggio non li usa.` : "Cliente aggiornato");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center p-4 sm:p-8">
      <div className="bg-white rounded-md w-full max-w-2xl p-6 my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold">Modifica cliente</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="sm:col-span-2">
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Ragione sociale</label>
            <input value={f.business_name || ""} onChange={(e) => setF({ ...f, business_name: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>
          <input placeholder="P.IVA" value={f.vat_number || ""} onChange={(e) => setF({ ...f, vat_number: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
          <input placeholder="Codice fiscale" value={f.fiscal_code || ""} onChange={(e) => setF({ ...f, fiscal_code: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
          <input placeholder="Indirizzo" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className="h-11 sm:col-span-2 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          <input placeholder="Telefono" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Dominio / tenant</label>
            <input data-testid="edit-client-domain" value={f.domain || ""} onChange={(e) => setF({ ...f, domain: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Scadenza licenza</label>
            <input data-testid="edit-client-expiry" type="date" value={(f.license_expires_at || "").slice(0, 10)} onChange={(e) => setF({ ...f, license_expires_at: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>
        </div>
        <div className="mb-5">
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-3">Moduli piattaforma</label>
          <ModuleToggles value={f.modules} onChange={(modules) => setF({ ...f, modules })} testidPrefix="edit-mod" />
        </div>
        <div className="mb-5">
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Attività del cliente</label>
          <select
            data-testid="edit-client-activity"
            value={f.activity_type || ""}
            onChange={(e) => {
              const activity_type = e.target.value;
              const mapped = ACTIVITY_TO_MODES[activity_type];
              setF({ ...f, activity_type, modes: mapped ? [...mapped] : (f.modes || []) });
            }}
            className="w-full h-12 px-3 border-2 border-velta-border rounded-md text-sm bg-white outline-none focus:border-velta-ink"
          >
            <option value="">— Seleziona attività —</option>
            {(Array.isArray(GROUPS) ? GROUPS : []).map((g) => (
              <optgroup key={g.id} label={g.label}>
                {(ADMIN_ACTIVITIES[g.id] || []).map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </optgroup>
            ))}
          </select>
          {f.activity_type === "alloggio_dedicato" ? (
            <p className="text-[11px] text-velta-mute mt-1.5">Alloggio dedicato: solo clienti, scansione e invio ad Alloggiati Web e ROSS 1000. Niente camere né prenotazioni. I clienti già salvati restano.</p>
          ) : f.activity_type === "bnb" ? (
            <p className="text-[11px] text-velta-mute mt-1.5">Alloggi / Pensioni. Si può riportare il cliente su Alberghiero dedicato da questo menu: camere e clienti non si cancellano.</p>
          ) : null}
        </div>
        <div className="mb-5">
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-3">Modalità attive ({(f.modes || []).length})</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-auto pos-scroll p-1">
            {(Array.isArray(modes) ? modes : []).map(m => (
              <button key={m.id} onClick={() => toggle(m.id)} className={`relative text-left p-3 rounded-md border transition-all ${(f.modes || []).includes(m.id) ? "border-[#1E2D5B] bg-[#1E2D5B]/5" : "border-velta-border bg-white hover:border-velta-ink"}`}>
                {(f.modes || []).includes(m.id) && <div className="absolute top-2 right-2 w-5 h-5 bg-[#2F9E77] rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
                <div className="font-medium text-sm pr-6">{m.label}</div>
                <div className="text-[10px] text-velta-mute mt-0.5 leading-snug">{m.description}</div>
              </button>
            ))}
          </div>
        </div>
        <button data-testid="edit-client-save" onClick={save} className="w-full h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Salva modifiche</button>
      </div>
      </div>
    </div>
  );
}

function ContractModal({ tenantId, businessName, onClose, onSaved }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/admin/clients/${tenantId}/contract`);
      setC(r.data);
    } catch { toast.error("Errore caricamento contratto"); }
  };
  useEffect(() => { load(); }, [tenantId]);

  const patch = (partial) => setC({ ...c, ...partial });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/clients/${tenantId}/contract`, {
        activity_type: c.activity_type,
        billing_cycle: c.billing_cycle,
        agreed_amount_eur: c.agreed_amount_eur == null || c.agreed_amount_eur === '' ? null : parseFloat(c.agreed_amount_eur),
        active_optional_modules: c.active_optional_modules || [],
        contract_status: c.contract_status,
        next_due_date: c.next_due_date || null,
        license_expires_at: c.next_due_date || c.license_expires_at || null,
        payment_method: c.payment_method,
        payment_status: c.payment_status,
        activation_date: c.activation_date || null,
        contract_notes: c.contract_notes || null,
        domain: c.domain || undefined,
        modules: c.modules,
      });
      const n = r.data?.catalog_removed?.products || 0;
      toast.success(n ? `Contratto salvato. Tolti ${n} articoli della cassa.` : "Contratto salvato");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const activate = async () => {
    try {
      await api.post(`/admin/clients/${tenantId}/activate`);
      toast.success("Cassa attivata — cliente può ora accedere");
      await load();
    } catch { toast.error("Errore attivazione"); }
  };
  const suspend = async () => {
    if (!window.confirm("Sospendere la cassa? Il cliente non potrà accedere.")) return;
    try {
      await api.post(`/admin/clients/${tenantId}/suspend`);
      toast.success("Cassa sospesa");
      await load();
    } catch { toast.error("Errore sospensione"); }
  };

  if (!c) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-velta-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Contratto</div>
            <h3 className="font-heading text-2xl font-semibold flex items-center gap-2"><FileText className="w-6 h-6" /> {businessName}</h3>
          </div>
          <button onClick={onClose} className="text-velta-mute hover:text-velta-ink"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Attività */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Attività del cliente</label>
            <select
              data-testid="contract-activity-select"
              value={c.activity_type || ""}
              onChange={(e) => patch({ activity_type: e.target.value })}
              className="w-full h-12 px-3 border-2 border-velta-border rounded-md text-sm bg-white outline-none focus:border-velta-ink"
            >
              <option value="">— Seleziona attività —</option>
              {(Array.isArray(GROUPS) ? GROUPS : []).map(g => (
                <optgroup key={g.id} label={g.label}>
                  {(ADMIN_ACTIVITIES[g.id] || []).map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </optgroup>
              ))}
            </select>
            {c.activity_type === "bnb" ? (
              <div className="text-[11px] text-velta-mute mt-1.5">Solo Alloggi: anagrafica, statistiche struttura, Alloggiati Web e ROSS 1000. Gli articoli della cassa non restano. I clienti stanno nel menu Clienti. Per il servizio dedicato scegli «Alberghiero dedicato»: camere e clienti restano.</div>
            ) : c.activity_type === "alloggio_dedicato" ? (
              <div className="text-[11px] text-velta-mute mt-1.5">Solo clienti: scansione del documento, giorni di permanenza, invio ad Alloggiati Web e ROSS 1000. Non ci sono camere né prenotazioni.</div>
            ) : c.activity_type && ACTIVITY_TO_MODES[c.activity_type] && (
              <div className="text-[11px] text-velta-mute mt-1.5">Modalità cassa: {ACTIVITY_TO_MODES[c.activity_type].join(", ")}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Dominio / tenant</label>
              <input
                data-testid="contract-domain"
                value={c.domain || ""}
                onChange={(e) => patch({ domain: e.target.value })}
                className="w-full h-11 px-3 border border-velta-border rounded-md font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Scadenza licenza</label>
              <input
                data-testid="contract-license-expiry"
                type="date"
                value={(c.next_due_date || c.license_expires_at || "").slice(0, 10)}
                onChange={(e) => patch({ next_due_date: e.target.value, license_expires_at: e.target.value })}
                className="w-full h-11 px-3 border border-velta-border rounded-md"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Moduli piattaforma</label>
            <ModuleToggles value={c.modules} onChange={(modules) => patch({ modules })} testidPrefix="contract-mod" />
          </div>

          <div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Ciclo di fatturazione</label>
              <div className="grid grid-cols-2 gap-2">
                {[{id: 'mensile', label: 'Mensile'}, {id: 'annuale', label: 'Annuale'}].map(b => (
                  <button
                    key={b.id}
                    data-testid={`contract-cycle-${b.id}`}
                    onClick={() => patch({ billing_cycle: b.id })}
                    className={`h-11 border-2 rounded-md text-sm font-medium transition ${c.billing_cycle === b.id ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border hover:border-velta-ink"}`}
                  >{b.label}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Importo pattuito */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Importo pattuito (€)</label>
              <input
                data-testid="contract-amount"
                type="number"
                step="0.01"
                min="0"
                value={c.agreed_amount_eur ?? ''}
                onChange={(e) => patch({ agreed_amount_eur: e.target.value })}
                placeholder="es. 49.00 (mese) o 490.00 (anno)"
                className="w-full h-11 px-3 border border-velta-border rounded-md font-mono text-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Prossima scadenza</label>
              <input
                data-testid="contract-due"
                type="date"
                value={c.next_due_date || ''}
                onChange={(e) => patch({ next_due_date: e.target.value })}
                className="w-full h-11 px-3 border border-velta-border rounded-md"
              />
            </div>
          </div>

          {/* Metodo & stato pagamento */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Metodo pagamento</label>
              <select
                data-testid="contract-payment-method"
                value={c.payment_method || ''}
                onChange={(e) => patch({ payment_method: e.target.value })}
                className="w-full h-11 px-3 border border-velta-border rounded-md text-sm bg-white"
              >
                <option value="">—</option>
                <option value="bonifico">Bonifico</option>
                <option value="riba">RiBa</option>
                <option value="carta">Carta</option>
                <option value="contanti">Contanti</option>
                <option value="altro">Altro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Stato pagamento</label>
              <select
                data-testid="contract-payment-status"
                value={c.payment_status || ''}
                onChange={(e) => patch({ payment_status: e.target.value })}
                className="w-full h-11 px-3 border border-velta-border rounded-md text-sm bg-white"
              >
                <option value="">—</option>
                <option value="pending">In attesa</option>
                <option value="pagato">Pagato</option>
                <option value="in_ritardo">In ritardo</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Stato contratto</label>
              <select
                data-testid="contract-status"
                value={c.contract_status || 'bozza'}
                onChange={(e) => patch({ contract_status: e.target.value })}
                className="w-full h-11 px-3 border border-velta-border rounded-md text-sm bg-white"
              >
                <option value="bozza">Bozza</option>
                <option value="attivo">Attivo</option>
                <option value="sospeso">Sospeso</option>
                <option value="scaduto">Scaduto</option>
              </select>
            </div>
          </div>

          {/* Note contratto */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Note contratto (uso interno)</label>
            <textarea
              data-testid="contract-notes"
              rows={2}
              value={c.contract_notes || ''}
              onChange={(e) => patch({ contract_notes: e.target.value })}
              placeholder="Es. Sconto applicato per fedeltà, hardware già venduto, accordi particolari..."
              className="w-full p-3 border border-velta-border rounded-md text-sm resize-none"
            />
          </div>

          {/* Riepilogo feature attive (calcolate server-side) */}
          {c.features && c.features.length > 0 && (
            <div className="bg-velta-bg rounded-md p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1.5">Feature attive per il cliente</div>
              <div className="flex flex-wrap gap-1">
                {(Array.isArray(c.features) ? c.features : []).map(f => (
                  <span key={f} className="text-[10px] font-mono bg-white border border-velta-border px-1.5 py-0.5 rounded">{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-velta-border flex items-center gap-2 sticky bottom-0 bg-white">
          <button
            data-testid="contract-copy-quote"
            onClick={async () => {
              const activityLabel = ACTIVITY_LABELS[c.activity_type] || "—";
              const cycleLabel = c.billing_cycle === "mensile" ? "al mese" : c.billing_cycle === "annuale" ? "all'anno" : "";
              const modLabels = (Array.isArray(c.active_optional_modules) ? c.active_optional_modules : []).map(m => c.available_optional_modules?.find(o => o.id === m)?.label).filter(Boolean);
              const text = `Preventivo VELTA SISTEMI per ${businessName}\n\n` +
                `Attività: ${activityLabel}\n` +
                (c.billing_cycle ? `Ciclo: ${c.billing_cycle.charAt(0).toUpperCase() + c.billing_cycle.slice(1)}\n` : "") +
                (modLabels.length > 0 ? `Moduli opzionali: ${modLabels.join(", ")}\n` : "") +
                `\nCanone software: ${c.agreed_amount_eur != null ? parseFloat(c.agreed_amount_eur).toFixed(2) + " € " + cycleLabel : "da concordare"}\n` +
                (c.next_due_date ? `Prima scadenza: ${new Date(c.next_due_date).toLocaleDateString("it-IT")}\n` : "") +
                (c.payment_method ? `Metodo pagamento: ${c.payment_method}\n` : "") +
                `\nHardware e attivazione una-tantum sono fatturati separatamente e non rientrano nel canone software.\n\n` +
                `Per accettare il preventivo rispondi a questo messaggio.`;
              try { await navigator.clipboard.writeText(text); toast.success("Preventivo copiato — incollalo su email o WhatsApp"); }
              catch { toast.error("Copia manuale richiesta"); }
            }}
            className="h-11 px-4 border border-velta-border rounded-md font-medium text-sm inline-flex items-center gap-2 hover:border-velta-ink"
          >
            <Copy className="w-4 h-4" /> Copia preventivo
          </button>
          {c.contract_status !== 'attivo' && (
            <button
              data-testid="contract-activate-btn"
              onClick={activate}
              className="h-11 px-4 bg-velta-success text-white rounded-md font-medium btn-tactile inline-flex items-center gap-2"
            ><Play className="w-4 h-4" /> Attiva cassa</button>
          )}
          {c.contract_status === 'attivo' && (
            <button
              data-testid="contract-suspend-btn"
              onClick={suspend}
              className="h-11 px-4 border border-velta-warning text-velta-warning rounded-md font-medium btn-tactile inline-flex items-center gap-2 hover:bg-yellow-50"
            ><Pause className="w-4 h-4" /> Sospendi</button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="h-11 px-4 border border-velta-border rounded-md font-medium">Annulla</button>
          <button
            data-testid="contract-save-btn"
            onClick={save}
            disabled={saving}
            className="h-11 px-6 bg-velta-ink text-white rounded-md font-medium btn-tactile disabled:opacity-40"
          >{saving ? "Salvo…" : "Salva contratto"}</button>
        </div>
      </div>
    </div>
  );
}

